/**
 * Genera la URL completa para un recurso multimedia.
 * Maneja rutas relativas, absolutas y recursos estáticos vs subidos al backend.
 * 
 * @param {string|object} path - La ruta de la imagen (ej: "users/123/posts/foto.jpg" o "/assets/logo.png")
 * @param {string} fallbackUrl - URL de fallback cuando el valor es invalido.
 * @returns {string} - La URL absoluta lista para usar en <img src="..." />
 */
export const DEFAULT_AVATAR_URL = '/images/avatar/DefaultProfile.jpg';
export const DEFAULT_COVER_URL = '/images/avatar/p-cover.jpg';
export const DEFAULT_MEDIA_URL = '/images/placeholder/user-80.svg';

const resolveMediaPath = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value.url || value.path || value.secure_url || value.mediaUrl || value.src || '';
  }
  return '';
};

const resolveApiOrigin = () => {
  const raw = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '';

  if (!raw || raw.startsWith('/')) {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }

  try {
    const parsed = new URL(raw);
    return parsed.origin;
  } catch {
    return '';
  }
};

export const withImageFallback = (event, fallbackUrl = DEFAULT_MEDIA_URL) => {
  if (!event?.currentTarget) return;
  event.currentTarget.onerror = null;
  event.currentTarget.src = fallbackUrl;
};

export const getMediaUrl = (path, fallbackUrl = DEFAULT_AVATAR_URL) => {
  const resolvedPath = resolveMediaPath(path).trim();
  if (!resolvedPath) return fallbackUrl;
  if (resolvedPath.startsWith('http://') || resolvedPath.startsWith('https://')) return resolvedPath;

  const apiOrigin = resolveApiOrigin();
  let normalizedPath = (resolvedPath.startsWith('/') ? resolvedPath.slice(1) : resolvedPath)
    .replace(/^\/+/, '')
    .replace(/\\/g, '/');

  // Fix rutas con prefijo accidental de perfil (ej: profile/username/users/...)
  if (normalizedPath.startsWith('profile/')) {
    const usersIndex = normalizedPath.indexOf('/users/');
    const uploadsIndex = normalizedPath.indexOf('/uploads/');
    if (usersIndex !== -1) {
      normalizedPath = normalizedPath.slice(usersIndex + 1);
    } else if (uploadsIndex !== -1) {
      normalizedPath = normalizedPath.slice(uploadsIndex + 1);
    } else {
      normalizedPath = normalizedPath.replace(/^profile\//, '');
    }
  }

  if (normalizedPath.startsWith('api/uploads/')) {
    normalizedPath = normalizedPath.replace(/^api\//, '');
  }

  if (normalizedPath.includes('/uploads/')) {
    const uploadsPath = normalizedPath.slice(normalizedPath.indexOf('uploads/'));
    return apiOrigin ? `${apiOrigin}/${uploadsPath}` : `/${uploadsPath}`;
  }

  if (normalizedPath.startsWith('users/')) {
    return apiOrigin ? `${apiOrigin}/uploads/${normalizedPath}` : `/uploads/${normalizedPath}`;
  }

  if (normalizedPath.startsWith('uploads/')) {
    return apiOrigin ? `${apiOrigin}/${normalizedPath}` : `/${normalizedPath}`;
  }

  if (normalizedPath.startsWith('images/') || normalizedPath.startsWith('sounds/')) {
    return `/${normalizedPath}`;
  }

  return `/${normalizedPath}`.replace(/\/+/g, '/');
};